import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  Chip,
  Card,
  CardContent,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Close as CloseIcon,
  Notes as NotesIcon,
  AccessTime as TimeIcon,
  ErrorOutline as ErrorIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { Test, getTestData, getClassificationsFromTest, getTestTypeCode, isScoreBasedTest } from '../types/test';
import { testService } from '../services/testService';
import { sileo } from 'sileo';
import MocaDetailedView from './MocaDetailedView';

/** Construye la URL pública de una imagen guardada en S3 (servida por /api/media). Usa el mismo origen que el API. */
function getMediaImageUrl(storedUrl: string | null | undefined): string {
  if (!storedUrl || typeof storedUrl !== 'string') return '';
  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
  const apiOrigin = new URL(apiBase).origin;
  try {
    const u = new URL(storedUrl, apiOrigin);
    return apiOrigin + u.pathname + (u.search || '');
  } catch {
    const path = storedUrl.startsWith('/') ? storedUrl : '/' + storedUrl;
    return apiOrigin + path;
  }
}

/** Nombres de los 18 elementos de la Figura Compleja de Rey (para tabla en vista guardada) */
const REY_ELEMENTOS = [
  'Cruz exterior, ángulo superior izquierdo',
  'Rectángulo grande, armazón de la figura',
  'Cruz de San Andrés, diagonal del rectángulo grande',
  'Mediana horizontal del rectángulo grande 2',
  'Mediana vertical del rectángulo grande 2',
  'Rectángulo pequeño en rectángulo grande',
  'Segmento pequeño sobre el rectángulo 6',
  '4 líneas paralelas en triángulo superior izquierdo',
  'Triángulo rectángulo sobre rectángulo grande (a la derecha)',
  'Pequeña perpendicular en cuadrante superior derecha',
  'Círculo con tres puntos en cuadrante superior derecho',
  '5 líneas paralelas en cuadrante inferior derecha',
  'Dos lados externos del triángulo isósceles de la derecha',
  'Pequeño rombo en vértice extremo del triángulo 13',
  'Segmento vertical en el interior del triángulo 13',
  'Prolongación de la mediana horizontal, altura de triángulo 13',
  'Cruz en extremo inferior del rectángulo 2',
  'Cuadrado y diagonal en extremo inferior izquierdo'
];

interface ObservationsModalProps {
  open: boolean;
  onClose: () => void;
  test: Test;
  patientName: string;
  onTestUpdated?: (updatedTest: Test) => void;
}

const ObservationsModal: React.FC<ObservationsModalProps> = ({
  open,
  onClose,
  test,
  patientName,
  onTestUpdated
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [observations, setObservations] = useState(test.observations || '');
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<{ open: boolean; url: string | null; title: string }>({ open: false, url: null, title: '' });

  // Actualizar observaciones cuando cambia el test
  useEffect(() => {
    setObservations(test.observations || '');
    setIsEditing(false);
  }, [test]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setObservations(test.observations || '');
    setIsEditing(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updatedTest = await testService.updateTest(test.id, {
        observations: observations
      });
      setIsEditing(false);
      if (onTestUpdated) {
        onTestUpdated(updatedTest);
      }
      sileo.success({
        title: 'Observaciones guardadas',
        description: 'Los cambios se guardaron correctamente.',
      });
    } catch (error: any) {
      console.error('Error al guardar observaciones:', error);
      sileo.error({
        title: 'No se pudieron guardar las observaciones',
        description: error.response?.data?.message || 'Ocurrió un error inesperado. Intenta nuevamente.',
      });
    } finally {
      setLoading(false);
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const testData = getTestData(test);
  const classifications = getClassificationsFromTest(test);
  const testTypeName = test.testType?.name || 'Prueba';
  const testTypeCode = getTestTypeCode(test);
  const isStroop = testTypeCode === 'STROOP';
  const isHVLTR = testTypeCode === 'HVLT-R';
  const isReyFigure = testTypeCode === 'REY-FIGURE';
  const isD2 = testTypeCode === 'D2';
  const isElevator3B = testTypeCode === 'ELEVATOR-3B';
  const isMoca = testTypeCode === 'MOCA';
  const evaluatorName = test.evaluator ? `${test.evaluator.firstName} ${test.evaluator.lastName}` : 'No especificado';

  // Colores de clasificación (mismos que la matriz de conversión)
  const classificationColors: { [key: string]: string } = {
    'Excepcionalmente Alto': '#dc6eee',
    'Sobre el Promedio': '#2196F3',
    'Promedio Alto': '#6dc7f0',
    'Promedio': '#4CAF50',
    'Promedio Bajo': '#FFEB3B',
    'Bajo el Promedio': '#FF9800',
    'Excepcionalmente Bajo': '#F44336'
  };

  // Función para determinar si un color es claro u oscuro (para contrastar texto)
  const getContrastTextColor = (hexColor: string): string => {
    // Remover el # si existe
    const color = hexColor.replace('#', '');
    
    // Convertir hex a RGB
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    
    // Calcular luminancia relativa (fórmula W3C)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Si la luminancia es mayor a 0.5, el color es claro, usar texto negro
    // Si es menor a 0.5, el color es oscuro, usar texto blanco
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // Helper para renderizar clasificaciones
  const renderClassifications = (classificationsList: string[]) => {
    if (classificationsList.length === 0) return null;
    if (classificationsList.length === 1) {
      const bgColor = classificationColors[classificationsList[0]] || '#757575';
      const textColor = getContrastTextColor(bgColor);
      
      return (
        <Chip 
          label={classificationsList[0]}
          size="small"
          variant="filled"
          sx={{
            bgcolor: bgColor,
            color: textColor,
            fontWeight: 'bold'
          }}
        />
      );
    }
    
    // Para múltiples clasificaciones
    const color1 = classificationColors[classificationsList[0]] || '#757575';
    const color2 = classificationColors[classificationsList[1]] || '#757575';
    const textColor1 = getContrastTextColor(color1);
    const textColor2 = getContrastTextColor(color2);
    const finalTextColor = (textColor1 === '#000000' && textColor2 === '#000000') ? '#000000' : '#FFFFFF';
    
    return (
      <Chip 
        label={classificationsList.join(' / ')}
        size="small"
        variant="filled"
        sx={{
          background: `linear-gradient(90deg, ${color1} 0%, ${color2} 100%)`,
          color: finalTextColor,
          fontWeight: 'bold'
        }}
      />
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isReyFigure ? "xl" : isMoca ? "lg" : "md"}
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <AssessmentIcon color="primary" />
          <Typography variant="h6">Detalle de Prueba</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Información General */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                Información General
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AssessmentIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      Tipo de Prueba:
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {testTypeName}
                  </Typography>
                </Box>
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      Paciente:
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {patientName}
                  </Typography>
                </Box>
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      Evaluador:
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {evaluatorName}
                  </Typography>
                </Box>
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CalendarIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      Fecha de Ejecución:
                    </Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(test.executedAt)}
                  </Typography>
                </Box>
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CalendarIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      Estado:
                    </Typography>
                  </Box>
                  <Chip 
                    label={test.status === 'completada' ? 'Completada' : test.status === 'en_progreso' ? 'En Progreso' : 'Cancelada'}
                    size="small"
                    color={test.status === 'completada' ? 'success' : test.status === 'en_progreso' ? 'warning' : 'error'}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Resultados de la Prueba */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                Resultados
              </Typography>
              
              {isHVLTR ? (
                // Vista para HVLT-R: Mostrar 4 ensayos horizontalmente, similar al formato de entrada
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* 4 Ensayos en layout horizontal */}
                  <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    {/* Ensayo 1 */}
                    {(testData.ensayo1Score !== undefined && testData.ensayo1Score !== null) && (
                      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                        <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                            Ensayo 1
                          </Typography>
                          
                          {/* Área de palabras - altura fija para 12 palabras sin scroll */}
                          <Box
                            sx={{
                              flex: 1,
                              overflowY: 'hidden',
                              minHeight: 0,
                              p: 1,
                              backgroundColor: 'background.paper',
                              borderRadius: 1,
                              mb: 1,
                              height: 430 // Altura fija para 12 palabras
                            }}
                          >
                            {testData.ensayo1Words && Array.isArray(testData.ensayo1Words) && testData.ensayo1Words.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {testData.ensayo1Words.map((word: string, index: number) => (
                                  <Chip
                                    key={`ensayo1-${word}-${index}`}
                                    label={`${index + 1}. ${word.charAt(0).toUpperCase() + word.slice(1)}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ justifyContent: 'flex-start' }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Sin palabras registradas
                              </Typography>
                            )}
                          </Box>

                          {/* Puntaje y estadísticas */}
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Chip 
                                label={`${testData.ensayo1Score} / 12`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                            {testData.ensayo1Percentile !== null && testData.ensayo1Percentile !== undefined && (
                              <Box sx={{ mb: 0.5 }}>
                                <Chip 
                                  label={`P${testData.ensayo1Percentile}`}
                                  size="small"
                                  color="info"
                                  variant="filled"
                                  sx={{ fontWeight: 'bold', mb: 0.5 }}
                                />
                              </Box>
                            )}
                            {testData.ensayo1Classifications && Array.isArray(testData.ensayo1Classifications) && testData.ensayo1Classifications.length > 0 && (
                              <Box>
                                {renderClassifications(testData.ensayo1Classifications)}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Ensayo 2 */}
                    {(testData.ensayo2Score !== undefined && testData.ensayo2Score !== null) && (
                      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                        <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                            Ensayo 2
                          </Typography>
                          
                          <Box
                            sx={{
                              flex: 1,
                              overflowY: 'hidden',
                              minHeight: 0,
                              p: 1,
                              backgroundColor: 'background.paper',
                              borderRadius: 1,
                              mb: 1,
                              height: 430
                            }}
                          >
                            {testData.ensayo2Words && Array.isArray(testData.ensayo2Words) && testData.ensayo2Words.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {testData.ensayo2Words.map((word: string, index: number) => (
                                  <Chip
                                    key={`ensayo2-${word}-${index}`}
                                    label={`${index + 1}. ${word.charAt(0).toUpperCase() + word.slice(1)}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ justifyContent: 'flex-start' }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Sin palabras registradas
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Chip 
                                label={`${testData.ensayo2Score} / 12`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                            {testData.ensayo2Percentile !== null && testData.ensayo2Percentile !== undefined && (
                              <Box sx={{ mb: 0.5 }}>
                                <Chip 
                                  label={`P${testData.ensayo2Percentile}`}
                                  size="small"
                                  color="info"
                                  variant="filled"
                                  sx={{ fontWeight: 'bold', mb: 0.5 }}
                                />
                              </Box>
                            )}
                            {testData.ensayo2Classifications && Array.isArray(testData.ensayo2Classifications) && testData.ensayo2Classifications.length > 0 && (
                              <Box>
                                {renderClassifications(testData.ensayo2Classifications)}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Ensayo 3 */}
                    {(testData.ensayo3Score !== undefined && testData.ensayo3Score !== null) && (
                      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                        <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                            Ensayo 3
                          </Typography>
                          
                          <Box
                            sx={{
                              flex: 1,
                              overflowY: 'hidden',
                              minHeight: 0,
                              p: 1,
                              backgroundColor: 'background.paper',
                              borderRadius: 1,
                              mb: 1,
                              height: 430
                            }}
                          >
                            {testData.ensayo3Words && Array.isArray(testData.ensayo3Words) && testData.ensayo3Words.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {testData.ensayo3Words.map((word: string, index: number) => (
                                  <Chip
                                    key={`ensayo3-${word}-${index}`}
                                    label={`${index + 1}. ${word.charAt(0).toUpperCase() + word.slice(1)}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ justifyContent: 'flex-start' }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Sin palabras registradas
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Chip 
                                label={`${testData.ensayo3Score} / 12`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                            {testData.ensayo3Percentile !== null && testData.ensayo3Percentile !== undefined && (
                              <Box sx={{ mb: 0.5 }}>
                                <Chip 
                                  label={`P${testData.ensayo3Percentile}`}
                                  size="small"
                                  color="info"
                                  variant="filled"
                                  sx={{ fontWeight: 'bold', mb: 0.5 }}
                                />
                              </Box>
                            )}
                            {testData.ensayo3Classifications && Array.isArray(testData.ensayo3Classifications) && testData.ensayo3Classifications.length > 0 && (
                              <Box>
                                {renderClassifications(testData.ensayo3Classifications)}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Ensayo 4 (Recuerdo Tardío) */}
                    {(testData.ensayo4Score !== undefined && testData.ensayo4Score !== null) && (
                      <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                        <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.main', height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle2" fontWeight="bold" color="info.dark" gutterBottom>
                            Ensayo 4 (Recuerdo Tardío)
                          </Typography>
                          
                          <Box
                            sx={{
                              flex: 1,
                              overflowY: 'hidden',
                              minHeight: 0,
                              p: 1,
                              backgroundColor: 'background.paper',
                              borderRadius: 1,
                              mb: 1,
                              height: 430
                            }}
                          >
                            {testData.ensayo4Words && Array.isArray(testData.ensayo4Words) && testData.ensayo4Words.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {testData.ensayo4Words.map((word: string, index: number) => (
                                  <Chip
                                    key={`ensayo4-${word}-${index}`}
                                    label={`${index + 1}. ${word.charAt(0).toUpperCase() + word.slice(1)}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ justifyContent: 'flex-start' }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Sin palabras registradas
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Chip 
                                label={`${testData.ensayo4Score} / 12`}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Recuerdo Total y Recuerdo Tardío horizontalmente */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, width: '100%' }}>
                    {/* Recuerdo Total */}
                    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                      <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', borderRadius: 1, border: '2px solid', borderColor: 'success.main', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                              Recuerdo Total
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                              Suma automática: Ensayo 1 + Ensayo 2 + Ensayo 3
                            </Typography>
                          </Box>
                          <Chip
                            label={`${testData.recuerdoTotalScore || 0} palabras`}
                            color="success"
                            sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 2.5, ml: 1, flexShrink: 0 }}
                          />
                        </Box>
                        
                        {/* Mostrar percentil y clasificación de Recuerdo Total */}
                        {testData.recuerdoTotalPercentile !== null && testData.recuerdoTotalPercentile !== undefined && (
                          <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                              <Chip
                                label={`P${testData.recuerdoTotalPercentile}`}
                                color="info"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                              {testData.recuerdoTotalClassifications && Array.isArray(testData.recuerdoTotalClassifications) && testData.recuerdoTotalClassifications.length > 0 && (
                                <Box>
                                  {renderClassifications(testData.recuerdoTotalClassifications)}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    
                    {/* Recuerdo Tardío */}
                    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                      <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', borderRadius: 1, border: '2px solid', borderColor: 'info.main', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="info.dark">
                              Recuerdo Tardío
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                              Ensayo 4 (20-25 minutos después)
                            </Typography>
                          </Box>
                          <Chip
                            label={`${testData.ensayo4Score || 0} palabras`}
                            color="info"
                            sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 2.5, ml: 1, flexShrink: 0 }}
                          />
                        </Box>
                        
                        {/* Mostrar percentil y clasificación de Recuerdo Tardío */}
                        {testData.recuerdoTardioPercentile !== null && testData.recuerdoTardioPercentile !== undefined && (
                          <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                              <Chip
                                label={`P${testData.recuerdoTardioPercentile}`}
                                color="info"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                              />
                              {testData.recuerdoTardioClassifications && Array.isArray(testData.recuerdoTardioClassifications) && testData.recuerdoTardioClassifications.length > 0 && (
                                <Box>
                                  {renderClassifications(testData.recuerdoTardioClassifications)}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : isStroop ? (
                // Vista para Stroop: Mostrar cada condición con su puntuación, percentil y clasificación
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Condición Palabra */}
                  {(testData.palabraScore !== undefined && testData.palabraScore !== null) && (
                    <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                        Condición: Palabra
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mt: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Puntuación:
                          </Typography>
                          <Chip 
                            label={`${testData.palabraScore} ítems correctos`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        {testData.palabraPercentile !== null && testData.palabraPercentile !== undefined && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Percentil:
                            </Typography>
                            <Chip 
                              label={`P${testData.palabraPercentile}`}
                              size="small"
                              color="info"
                              variant="filled"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                        )}
                        {testData.palabraClassifications && Array.isArray(testData.palabraClassifications) && testData.palabraClassifications.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Clasificación:
                            </Typography>
                            {renderClassifications(testData.palabraClassifications)}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Condición Color */}
                  {(testData.colorScore !== undefined && testData.colorScore !== null) && (
                    <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                        Condición: Color
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mt: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Puntuación:
                          </Typography>
                          <Chip 
                            label={`${testData.colorScore} ítems correctos`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        {testData.colorPercentile !== null && testData.colorPercentile !== undefined && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Percentil:
                            </Typography>
                            <Chip 
                              label={`P${testData.colorPercentile}`}
                              size="small"
                              color="info"
                              variant="filled"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                        )}
                        {testData.colorClassifications && Array.isArray(testData.colorClassifications) && testData.colorClassifications.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Clasificación:
                            </Typography>
                            {renderClassifications(testData.colorClassifications)}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Condición Palabra-Color */}
                  {(testData.palabraColorScore !== undefined && testData.palabraColorScore !== null) && (
                    <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                        Condición: Palabra-Color
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mt: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Puntuación:
                          </Typography>
                          <Chip 
                            label={`${testData.palabraColorScore} ítems correctos`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        {testData.palabraColorPercentile !== null && testData.palabraColorPercentile !== undefined && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Percentil:
                            </Typography>
                            <Chip 
                              label={`P${testData.palabraColorPercentile}`}
                              size="small"
                              color="info"
                              variant="filled"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                        )}
                        {testData.palabraColorClassifications && Array.isArray(testData.palabraColorClassifications) && testData.palabraColorClassifications.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Clasificación:
                            </Typography>
                            {renderClassifications(testData.palabraColorClassifications)}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Interferencia */}
                  {(testData.interferenciaScore !== undefined && testData.interferenciaScore !== null) && (
                    <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                        Índice de Interferencia
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mt: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Puntuación:
                          </Typography>
                          <Chip 
                            label={`${testData.interferenciaScore}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        {testData.interferenciaPercentile !== null && testData.interferenciaPercentile !== undefined && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Percentil:
                            </Typography>
                            <Chip 
                              label={`P${testData.interferenciaPercentile}`}
                              size="small"
                              color="info"
                              variant="filled"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                        )}
                        {testData.interferenciaClassifications && Array.isArray(testData.interferenciaClassifications) && testData.interferenciaClassifications.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Clasificación:
                            </Typography>
                            {renderClassifications(testData.interferenciaClassifications)}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : isReyFigure ? (
                // Vista Rey Figure guardada: 1) 3 imágenes en fila (clic para agrandar), 2) tabla, 3) resultados según baremos, observaciones editable más abajo
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* 1. Tres imágenes en fila, clic para agrandar */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {[
                      { url: getMediaImageUrl(testData.copiaImageUrl), title: 'Copia', alt: 'Figura de Rey - Copia' },
                      { url: getMediaImageUrl(testData.memoriaInmediataImageUrl), title: 'Memoria inmediata', alt: 'Figura de Rey - Memoria inmediata' },
                      { url: getMediaImageUrl(testData.memoriaDiferidaImageUrl), title: 'Memoria diferida', alt: 'Figura de Rey - Memoria diferida' }
                    ].map(({ url, title, alt }) => (
                      <Box key={title} sx={{ flex: '1 1 0', minWidth: 160, maxWidth: 320 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom align="center">{title}</Typography>
                        {url ? (
                          <Box
                            component="img"
                            src={url}
                            alt={alt}
                            onClick={() => setImagePreview({ open: true, url, title: `Figura de Rey (${title})` })}
                            sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { boxShadow: 2, borderColor: 'primary.main' } }}
                          />
                        ) : (
                          <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary">Sin imagen</Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Divider />

                  {/* 2. Tabla de puntuación (desde reyFigureElementos guardado) */}
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Tabla de puntuación</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Elementos</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '22%' }}>Copia</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '22%' }}>M. Inmed.</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', width: '22%' }}>M. Diferida</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(testData.reyFigureElementos && Array.isArray(testData.reyFigureElementos) && testData.reyFigureElementos.length > 0)
                          ? testData.reyFigureElementos.map((el: { numero: number; nombre: string; copia?: number | string | null; memoriaInmediata?: number | string | null; memoriaDiferida?: number | string | null }, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem', py: 0.4 }}>{el.numero}. {el.nombre}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.3 }}>{el.copia != null && el.copia !== '' ? String(el.copia) : '—'}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.3 }}>{el.memoriaInmediata != null && el.memoriaInmediata !== '' ? String(el.memoriaInmediata) : '—'}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.3 }}>{el.memoriaDiferida != null && el.memoriaDiferida !== '' ? String(el.memoriaDiferida) : '—'}</TableCell>
                              </TableRow>
                            ))
                          : REY_ELEMENTOS.map((label, row) => (
                              <TableRow key={row}>
                                <TableCell sx={{ fontSize: '0.7rem', py: 0.4 }}>{row + 1}. {label}</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.3 }}>—</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.3 }}>—</TableCell>
                                <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.3 }}>—</TableCell>
                              </TableRow>
                            ))}
                        <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>PUNTAJE TOTAL</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{testData.copiaPuntajeTotal ?? testData.copiaExactitud ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{testData.memoriaInmediataPuntajeTotal ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{testData.memoriaDiferidaPuntajeTotal ?? testData.memoriaExactitud ?? '—'}</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TIEMPO TOTAL (min)</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{testData.copiaTiempoMin ?? testData.copiaTiempo ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{testData.memoriaInmediataTiempoMin ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{testData.memoriaDiferidaTiempoMin ?? '—'}</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>TIPO DE COPIA</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{testData.copiaTipo ?? '—'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>—</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.8rem' }}>—</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />

                  {/* 3. Resultados según baremos: Puntaje / Percentil / Calificación uno debajo del otro */}
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Resultados según baremos</Typography>
                  <Box sx={{ display: 'flex', gap: 0, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'stretch', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">Exactitud Copia</Typography>
                      <Typography variant="body2">Puntaje: {testData.copiaExactitud ?? testData.copiaPuntajeTotal ?? 'N/A'}</Typography>
                      <Typography variant="body2">Percentil: {testData.copiaExactitudPercentile ?? 'N/A'}</Typography>
                      {(testData.copiaExactitudClassifications?.length ?? 0) > 0 ? (<><Typography variant="body2">Calificación:</Typography><Chip size="small" variant="filled" label={testData.copiaExactitudClassifications.join(' / ')} sx={{ bgcolor: classificationColors[testData.copiaExactitudClassifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }} /></>) : <Typography variant="body2" color="text.secondary">Calificación: —</Typography>}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">Tiempo Copia</Typography>
                      <Typography variant="body2">Puntaje: {testData.copiaTiempo ?? 'N/A'} min</Typography>
                      <Typography variant="body2">Percentil: {testData.copiaTiempoPercentile ?? 'N/A'}</Typography>
                      {(testData.copiaTiempoClassifications?.length ?? 0) > 0 ? (<><Typography variant="body2">Calificación:</Typography><Chip size="small" variant="filled" label={testData.copiaTiempoClassifications.join(' / ')} sx={{ bgcolor: classificationColors[testData.copiaTiempoClassifications[0]] || '#757575', color: 'white', fontWeight: 'bold' }} /></>) : <Typography variant="body2" color="text.secondary">Calificación: —</Typography>}
                    </Box>
                    {testData.copiaTipo && (
                      <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="bold">Tipo copia</Typography>
                        <Typography variant="body2">Puntaje: {testData.copiaTipo}</Typography>
                        <Typography variant="body2">Percentil: {testData.copiaTipoPercentile ?? testData.memoriaTipoConstruccionPercentile ?? 'N/A'}</Typography>
                        {((testData.copiaTipoClassifications ?? testData.memoriaTipoConstruccionClassifications)?.length ?? 0) > 0 ? (<><Typography variant="body2">Calificación:</Typography><Chip size="small" variant="filled" label={(testData.copiaTipoClassifications ?? testData.memoriaTipoConstruccionClassifications)!.join(' / ')} sx={{ bgcolor: classificationColors[(testData.copiaTipoClassifications ?? testData.memoriaTipoConstruccionClassifications)![0]] || '#757575', color: 'white', fontWeight: 'bold' }} /></>) : <Typography variant="body2" color="text.secondary">Calificación: —</Typography>}
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, borderRight: { xs: 'none', md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">Memoria inmediata</Typography>
                      <Typography variant="body2">Puntaje: {testData.memoriaInmediataPuntajeTotal ?? 'N/A'}</Typography>
                      <Typography variant="body2">Percentil: {testData.memoriaInmediataPercentile ?? 'N/A'}</Typography>
                      {(testData.memoriaInmediataClassifications?.length ?? 0) > 0 ? (<><Typography variant="body2">Calificación:</Typography><Chip size="small" variant="filled" label={testData.memoriaInmediataClassifications!.join(' / ')} sx={{ bgcolor: classificationColors[testData.memoriaInmediataClassifications![0]] || '#757575', color: 'white', fontWeight: 'bold' }} /></>) : <Typography variant="body2" color="text.secondary">Calificación: —</Typography>}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 'auto' }, px: 2, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">Memoria diferida</Typography>
                      <Typography variant="body2">Puntaje: {testData.memoriaExactitud ?? testData.memoriaDiferidaPuntajeTotal ?? 'N/A'}</Typography>
                      <Typography variant="body2">Percentil: {testData.memoriaDiferidaPercentile ?? testData.memoriaExactitudPercentile ?? 'N/A'}</Typography>
                      {((testData.memoriaDiferidaClassifications ?? testData.memoriaExactitudClassifications)?.length ?? 0) > 0 ? (<><Typography variant="body2">Calificación:</Typography><Chip size="small" variant="filled" label={(testData.memoriaDiferidaClassifications ?? testData.memoriaExactitudClassifications)!.join(' / ')} sx={{ bgcolor: classificationColors[(testData.memoriaDiferidaClassifications ?? testData.memoriaExactitudClassifications)![0]] || '#757575', color: 'white', fontWeight: 'bold' }} /></>) : <Typography variant="body2" color="text.secondary">Calificación: —</Typography>}
                    </Box>
                  </Box>
                </Box>
              ) : isD2 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    {[
                      { label: 'TR', score: testData.trScore, percentile: testData.trPercentile, classifications: testData.trClassifications },
                      { label: 'TA', score: testData.taScore, percentile: testData.taPercentile, classifications: testData.taClassifications },
                      { label: 'O', score: testData.oScore, percentile: testData.oPercentile, classifications: testData.oClassifications },
                      { label: 'C', score: testData.cScore, percentile: testData.cPercentile, classifications: testData.cClassifications },
                      { label: 'TR+', score: testData.trPlusScore, percentile: testData.trPlusPercentile, classifications: testData.trPlusClassifications },
                      { label: 'TR-', score: testData.trMinusScore, percentile: testData.trMinusPercentile, classifications: testData.trMinusClassifications },
                      { label: 'VAR', score: testData.varScore, percentile: testData.varPercentile, classifications: testData.varClassifications }
                    ].map((metric) => (
                      <Box key={metric.label} sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">{metric.label}</Typography>
                        <Typography variant="body2">Puntaje: {metric.score ?? '—'}</Typography>
                        <Typography variant="body2">Percentil: {metric.percentile ?? '—'}</Typography>
                        {metric.classifications && Array.isArray(metric.classifications) && metric.classifications.length > 0 ? (
                          renderClassifications(metric.classifications)
                        ) : (
                          <Typography variant="caption" color="text.secondary">Sin clasificación</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                      <Typography variant="body2" fontWeight="bold" color="success.dark">TOT</Typography>
                      <Typography variant="body2">Puntaje: {testData.totScore ?? '—'}</Typography>
                      <Typography variant="body2">Percentil: {testData.totPercentile ?? '—'}</Typography>
                      {testData.totClassifications && Array.isArray(testData.totClassifications) && testData.totClassifications.length > 0 ? (
                        renderClassifications(testData.totClassifications)
                      ) : (
                        <Typography variant="caption" color="text.secondary">Sin clasificación</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">Formula: TR - (O + C)</Typography>
                    </Box>

                    <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                      <Typography variant="body2" fontWeight="bold" color="info.dark">CON</Typography>
                      <Typography variant="body2">Puntaje: {testData.concentrationScore ?? '—'}</Typography>
                      <Typography variant="body2">Percentil: {testData.concentrationPercentile ?? '—'}</Typography>
                      {testData.concentrationClassifications && Array.isArray(testData.concentrationClassifications) && testData.concentrationClassifications.length > 0 ? (
                        renderClassifications(testData.concentrationClassifications)
                      ) : (
                        <Typography variant="caption" color="text.secondary">Sin clasificación</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">Formula: TA - C</Typography>
                    </Box>

                    <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.15)' : 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                      <Typography variant="body2" fontWeight="bold" color="warning.dark">
                        % Error
                      </Typography>
                      <Typography variant="body2">
                        Resultado: {testData.errorRate !== undefined && testData.errorRate !== null ? `${Number(testData.errorRate).toFixed(2)}%` : '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Formula: 100 * (O + C) / TR
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : isElevator3B ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    {
                      label: 'Versión A',
                      score: testData.elevatorVersionAScore,
                      max: 7,
                      percentile: testData.elevatorVersionAPercentile,
                      classifications: testData.elevatorVersionAClassifications || [],
                    },
                    {
                      label: 'Versión B',
                      score: testData.elevatorVersionBScore,
                      max: 10,
                      percentile: testData.elevatorVersionBPercentile,
                      classifications: testData.elevatorVersionBClassifications || [],
                    },
                  ].map((version) => (
                    <Box key={version.label} sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                        {version.label}
                      </Typography>
                      <Typography variant="body2">Aciertos: {version.score ?? '—'} / {version.max}</Typography>
                      <Typography variant="body2">Percentil: {version.percentile ?? '—'}</Typography>
                      {version.classifications.length > 0 ? renderClassifications(version.classifications) : (
                        <Typography variant="caption" color="text.secondary">Sin clasificación</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : isMoca ? (
                // Vista para MoCA: puntaje/percentil + detalle completo por dominio (marcado/no marcado)
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.50', borderRadius: 1, border: '2px solid', borderColor: 'success.main' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                          Puntaje Directo Total
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total: {testData.mocaScore ?? '--'} / 30
                        </Typography>
                      </Box>
                      <Chip
                        label={testData.mocaScore ?? '--'}
                        color="success"
                        sx={{ fontSize: '1.5rem', fontWeight: 'bold', px: 3, py: 3 }}
                      />
                    </Box>
                    {testData.percentile !== null && testData.percentile !== undefined && (
                      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={2} pt={2} borderTop="1px solid" borderColor="divider">
                        <Chip
                          label={`Percentil: ${testData.percentile}`}
                          color="info"
                          size="medium"
                          variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                        />
                        {classifications.length > 0 && renderClassifications(classifications)}
                      </Box>
                    )}
                    {testData.mocaEducationAdjustment !== null && testData.mocaEducationAdjustment !== undefined && testData.mocaEducationAdjustment > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {`* Puntaje ajustado por escolaridad: ${testData.mocaRawScore} + ${testData.mocaEducationAdjustment} = ${testData.mocaAdjustedScore} / 30 (usado para el percentil)`}
                      </Typography>
                    )}
                  </Box>

                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Detalle por dominio — lo marcado y no marcado
                  </Typography>
                  <MocaDetailedView data={testData.mocaDetailed} />
                </Box>
              ) : (
                // Vista para TMT: Mostrar tiempo, errores, percentil y clasificación
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {testData.timeSeconds > 0 && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <TimeIcon fontSize="small" color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          Tiempo:
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${testData.timeSeconds}s`}
                        size="medium"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  )}
                  {testData.errors !== null && testData.errors !== undefined && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <ErrorIcon fontSize="small" color="error" />
                        <Typography variant="body2" color="text.secondary">
                          Errores:
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${testData.errors} ${testData.errors === 1 ? 'error' : 'errores'}`}
                        size="medium"
                        color={testData.errors > 0 ? 'error' : 'success'}
                        variant="outlined"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  )}
                  {classifications.length > 0 && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <AssessmentIcon fontSize="small" color="info" />
                        <Typography variant="body2" color="text.secondary">
                          Clasificación:
                        </Typography>
                      </Box>
                      {renderClassifications(classifications)}
                    </Box>
                  )}
                  {testData.percentile !== null && testData.percentile !== undefined && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <AssessmentIcon fontSize="small" color="info" />
                        <Typography variant="body2" color="text.secondary">
                          Percentil:
                        </Typography>
                      </Box>
                      <Chip 
                        label={`P${testData.percentile}`}
                        size="medium"
                        color="info"
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  )}
                  {testData.educationYears !== null && testData.educationYears !== undefined && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <PersonIcon fontSize="small" color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          Años de Escolaridad:
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="medium">
                        {testData.educationYears} años
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Observaciones */}
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <NotesIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Observaciones
                  </Typography>
                </Box>
                {!isEditing ? (
                  <IconButton 
                    size="small" 
                    onClick={handleEdit}
                    color="primary"
                    sx={{ ml: 'auto' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                      variant="outlined"
                      color="secondary"
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      variant="contained"
                      color="primary"
                      disabled={loading}
                    >
                      Guardar
                    </Button>
                  </Box>
                )}
              </Box>
              {isEditing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  placeholder="Ingrese las observaciones..."
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              ) : (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    p: 2,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    minHeight: '80px'
                  }}
                >
                  {test.observations || 'Sin observaciones registradas.'}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Información del Sistema */}
          <Card variant="outlined" sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Información del Sistema
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Creado:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDateTime(test.createdAt)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Última Actualización:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDateTime(test.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>

      {/* Modal para agrandar imagen (Rey Figure) */}
      <Dialog
        open={imagePreview.open}
        onClose={() => setImagePreview({ open: false, url: null, title: '' })}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.9)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white' }}>
          <Typography variant="h6">{imagePreview.title}</Typography>
          <IconButton onClick={() => setImagePreview({ open: false, url: null, title: '' })} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
          {imagePreview.url && (
            <img src={imagePreview.url} alt={imagePreview.title} style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }} />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ObservationsModal;


